import { md5 } from 'js-md5';
import { seriesKey } from '../../../shared/utils/vendor-series-key.util';
import type { VendorSlug } from '../../../shared/vendor/vendor-slug';
import { toMasked } from '../shared/masked-ean';
import type { RegionalSalesQuery, RegionGrain } from './regional-sales';

/**
 * The cache key a set of query parameters answers to.
 *
 * This is a wire contract in both directions: the backend derives the same key from the
 * job params it is warmed with, so a byte for byte difference here does not read as a miss
 * on one row -- it silently orphans every row already in the cache and warms a parallel
 * key space that nothing reads back. The canonical string is therefore reproduced exactly
 * as `loadRegionalAggregation` has always built it, spacing included, and
 * `regional-cache-key.spec.ts` pins it byte for byte against a hardcoded literal.
 */

/** The feed's name for each grain. `sweden_municipalities` means LANDSKAP, not kommuner. */
const REGION_SET_BY_GRAIN: Readonly<Record<RegionGrain, string>> = {
  landskap: 'sweden_municipalities',
  kommun: 'kommuner',
};

/** The one granularity the feed is built at: every cell covers an ISO week. */
const GRANULARITY = 'weekly';

/** The backend cuts every region in the set; no caller has ever narrowed it. */
const ALL_REGIONS: readonly string[] = [];

/**
 * The key form this feed lists a chain under, both in the cache key and in the payload.
 *
 * The suffix itself is not spelled here: `seriesKey` is the one place the `-retail` form is
 * produced (AC-14), and this feed's in-store keys are that same form. What this adds is the
 * brand: taking a `VendorSlug` means a wire key cannot be appended to a second time, since
 * `kronan-retail` handed in here would key `kronan-retail-retail`, a row that exists nowhere
 * and reads back as "no sales".
 */
export function inStoreVendorKey(vendor: VendorSlug): string {
  return seriesKey(vendor, 'in-store');
}

/**
 * The canonical parameter string the cache key is the md5 of.
 *
 * Sorted at every level -- the object's keys, the vendor list, the EAN list -- so that two
 * callers asking the same question in a different order land on the same row. The trailing
 * `replace`s are not cosmetic: they reproduce the exact spacing the key has always been
 * hashed with, and they run over the whole serialized string, nested objects included.
 */
export function regionalCacheString(query: RegionalSalesQuery): string {
  const params: Record<string, unknown> = {
    date_start: query.dateStart,
    date_end: query.dateEnd,
    vendors: query.vendors.map(inStoreVendorKey).sort(),
    region_set: REGION_SET_BY_GRAIN[query.grain],
    region_ids: ALL_REGIONS,
    product_filter: { type: 'eans', values: query.eans.map(toMasked).sort() },
    granularity: GRANULARITY,
  };

  const ordered: Record<string, unknown> = {};
  for (const key of Object.keys(params).sort()) ordered[key] = params[key];
  return JSON.stringify(ordered).replace(/,/g, ', ').replace(/:/g, ': ');
}

/**
 * The job parameters the backend aggregates a query under.
 *
 * The same wire contract as the cache key, from the other side: the backend derives the
 * row's `cache_key` from these params, so a warm submitted with different words than the
 * read looks up warms a row nothing reads back. Both forms are therefore built here, from
 * the same query, and neither is spelled anywhere above this module.
 *
 * `vendors` is deliberately NOT sorted, and the key's copy is: this reproduces exactly what
 * `loadRegionalAggregation` has always submitted. The backend sorts before it hashes, so the
 * two agree on the row either way -- but a params object that differs from today's in any
 * other way is a different request, and this one is not.
 */
export type RegionalAggregationJobParams = {
  readonly job_type: 'retail_regional_agg';
  readonly date_start: string;
  readonly date_end: string;
  readonly vendors: readonly string[];
  readonly region_set: string;
  readonly region_ids: readonly string[];
  readonly granularity: string;
  readonly product_filter: { readonly type: 'eans'; readonly values: readonly string[] };
};

export function regionalJobParams(query: RegionalSalesQuery): RegionalAggregationJobParams {
  return {
    job_type: 'retail_regional_agg',
    date_start: query.dateStart,
    date_end: query.dateEnd,
    vendors: query.vendors.map(inStoreVendorKey),
    region_set: REGION_SET_BY_GRAIN[query.grain],
    region_ids: ALL_REGIONS,
    granularity: GRANULARITY,
    product_filter: { type: 'eans', values: query.eans.map(toMasked).sort() },
  };
}

/** The `cache_key` column value for a query. */
export function regionalCacheKey(query: RegionalSalesQuery): string {
  return md5(regionalCacheString(query));
}
